import { useRef, useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';

import { LoggerFactory } from '@videoblocks/typescript-logger';

import { getLoadedPasswordScorer, loadPasswordScorer } from '../../../lib/validation/passwordStrength';
import { validatePasswordForSignUp } from '../../../lib/validation/passwordValidation';

const logger = LoggerFactory.create('useFormValidation');

interface FormValidationRefs {
    emailInputRef: React.RefObject<HTMLInputElement | null>;
    passwordInputRef: React.RefObject<HTMLInputElement | null>;
    checkboxRef: React.RefObject<HTMLInputElement | null>;
}

interface ValidationErrors {
    email: string;
    password: string;
    checkbox: string;
}

interface FieldFlags {
    email: boolean;
    password: boolean;
    checkbox: boolean;
}

interface UseFormValidationReturn {
    isFormValid: boolean;
    errors: ValidationErrors;
    touched: FieldFlags;
    focused: FieldFlags;
    refs: FormValidationRefs;
    handleInputChange: () => void;
    handleBlur: (field: keyof ValidationErrors) => void;
    handleFocus: (field: keyof ValidationErrors) => void;
    validateField: (field: keyof ValidationErrors, value: string | boolean) => string;
}

interface UseFormValidationOptions {
    validatePasswordStrength?: boolean;
    defaultValues?: {
        checkbox?: boolean;
    };
}

const NO_FIELDS: FieldFlags = { email: false, password: false, checkbox: false };
// A zxcvbn score can take a few hundred ms on the main thread, so it runs once typing pauses.
const SCORE_DEBOUNCE_MS = 250;

type EvaluationMode = 'typing' | 'full';

export function useFormValidation({
    validatePasswordStrength = true,
    defaultValues = {},
}: UseFormValidationOptions = {}): UseFormValidationReturn {
    const [isFormValid, setIsFormValid] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>({
        email: '',
        password: '',
        checkbox: '',
    });
    const [touched, setTouched] = useState<FieldFlags>({
        ...NO_FIELDS,
        checkbox: defaultValues.checkbox ?? false,
    });
    const [focused, setFocused] = useState<FieldFlags>(NO_FIELDS);

    const emailInputRef = useRef<HTMLInputElement>(null);
    const passwordInputRef = useRef<HTMLInputElement>(null);
    // defaultValues.checkbox pre-accepts the agreement so validity can be computed before a
    // checkbox is bound; once a real checkbox is rendered its DOM ref replaces this placeholder.
    const checkboxRef = useRef<HTMLInputElement>(
        defaultValues.checkbox ? ({ checked: defaultValues.checkbox } as HTMLInputElement) : null,
    );
    // Set once the zxcvbn chunk fails to load, so validity falls back to the baseline instead of
    // holding the submit button forever. user-service still enforces the score.
    const scorerUnavailableRef = useRef(false);
    // A field blurs on mouse-down, before the click completes. Applying the blur outcome
    // right then (hiding the requirements list, showing an error) shifts the layout, so the control
    // under the pointer moves away and the browser never fires its click. While a press is in
    // progress the outcome is parked here and applied once the pointer is released.
    const isPasswordFocusedRef = useRef(false);
    const hasPasswordBlurredRef = useRef(false);
    const isPointerDownRef = useRef(false);
    const pendingBlurRef = useRef<(() => void) | null>(null);
    const scoreTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isScorePendingRef = useRef(false);
    const lastScoredPasswordRef = useRef<{ value: string; error: string } | null>(null);
    const evaluateFormRef = useRef<(mode: EvaluationMode) => void>(() => undefined);

    const validateField = useCallback(
        (field: keyof ValidationErrors, value: string | boolean, options: { skipScore?: boolean } = {}): string => {
            switch (field) {
                case 'email': {
                    if (!value || typeof value !== 'string') {
                        return 'Please enter your email address.';
                    }
                    if (value.trim() === '') {
                        return 'Please enter your email address.';
                    }
                    const emailRegex =
                        /^[^\s@]+@[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
                    if (!emailRegex.test(value)) {
                        return 'Please enter a valid email address.';
                    }
                    return '';
                }

                case 'password': {
                    if (!value || typeof value !== 'string' || value.trim() === '') {
                        return 'Please enter your password.';
                    }
                    if (validatePasswordStrength) {
                        const scorer = options.skipScore ? null : getLoadedPasswordScorer();
                        const validation = validatePasswordForSignUp(value, scorer);
                        if (!validation.isValid) {
                            return validation.errors[0];
                        }
                    }
                    return '';
                }

                case 'checkbox':
                    if (!value) {
                        return 'This field is required.';
                    }
                    return '';

                default:
                    return '';
            }
        },
        [validatePasswordStrength],
    );

    const clearScoreTimer = useCallback(() => {
        if (scoreTimerRef.current) {
            clearTimeout(scoreTimerRef.current);
            scoreTimerRef.current = null;
        }
    }, []);

    const evaluateForm = useCallback(
        (mode: EvaluationMode) => {
            const emailInput = emailInputRef.current;
            const passwordInput = passwordInputRef.current;
            const checkbox = checkboxRef.current;
            if (!emailInput || !checkbox) {
                return;
            }

            const emailError = validateField('email', emailInput.value);
            const checkboxError = validateField('checkbox', checkbox.checked);

            // Password is only required if the field exists (not SSO flow)
            const passwordValue = passwordInput?.value ?? '';
            const needsScore = validatePasswordStrength && !!passwordInput && passwordValue !== '';
            const lastScored = lastScoredPasswordRef.current;

            let passwordError = '';
            if (passwordInput && needsScore && lastScored?.value === passwordValue) {
                passwordError = lastScored.error;
            } else if (passwordInput && needsScore && mode === 'typing') {
                passwordError = validateField('password', passwordValue, { skipScore: true });
                if (passwordError === '') {
                    isScorePendingRef.current = true;
                    clearScoreTimer();
                    scoreTimerRef.current = setTimeout(() => evaluateFormRef.current('full'), SCORE_DEBOUNCE_MS);
                    setIsFormValid(false);
                    return;
                }
            } else if (passwordInput) {
                passwordError = validateField('password', passwordValue);
                if (needsScore && getLoadedPasswordScorer()) {
                    lastScoredPasswordRef.current = { value: passwordValue, error: passwordError };
                }
            }

            isScorePendingRef.current = false;
            clearScoreTimer();

            // A baseline-valid password is unverified until its zxcvbn score is known, so the submit
            // button stays disabled while the scorer chunk is loading.
            const isScoreUnknown = needsScore && !getLoadedPasswordScorer() && !scorerUnavailableRef.current;

            // Only update form validity for submit button, don't update error messages
            setIsFormValid(!emailError && !passwordError && !checkboxError && !isScoreUnknown);
        },
        [validateField, validatePasswordStrength, clearScoreTimer],
    );

    useEffect(() => {
        evaluateFormRef.current = evaluateForm;
    }, [evaluateForm]);

    useEffect(() => clearScoreTimer, [clearScoreTimer]);

    const checkFormValidity = useCallback(() => evaluateForm('full'), [evaluateForm]);

    const flushPendingScore = useCallback(() => {
        if (isScorePendingRef.current) {
            evaluateForm('full');
        }
    }, [evaluateForm]);

    // A field blurred before the scorer arrived was validated against the baseline only, so its
    // complexity error would stay hidden until the next focus and blur.
    const revalidateBlurredPassword = useCallback(() => {
        const passwordInput = passwordInputRef.current;
        if (!passwordInput || isPasswordFocusedRef.current || !hasPasswordBlurredRef.current) {
            return;
        }
        const error = validateField('password', passwordInput.value);
        setErrors((prev) => ({ ...prev, password: error }));
    }, [validateField]);

    const ensureScorerLoaded = useCallback(() => {
        if (!validatePasswordStrength || getLoadedPasswordScorer() || scorerUnavailableRef.current) {
            return;
        }
        loadPasswordScorer()
            .then(() => {
                checkFormValidity();
                revalidateBlurredPassword();
            })
            .catch((error: unknown) => {
                logger.warn({ err: error }, 'Password strength scorer failed to load; deferring to server');
                scorerUnavailableRef.current = true;
                checkFormValidity();
            });
    }, [validatePasswordStrength, checkFormValidity, revalidateBlurredPassword]);

    const handleFocus = useCallback(
        (field: keyof ValidationErrors) => {
            setTouched((prev) => ({ ...prev, [field]: true }));
            setFocused((prev) => ({ ...prev, [field]: true }));

            if (field === 'password') {
                isPasswordFocusedRef.current = true;
                ensureScorerLoaded();
            }
        },
        [ensureScorerLoaded],
    );

    const handleBlur = useCallback(
        (field: keyof ValidationErrors) => {
            const input =
                field === 'checkbox'
                    ? checkboxRef.current
                    : field === 'email'
                      ? emailInputRef.current
                      : passwordInputRef.current;

            const applyBlur = (): void => {
                if (field === 'password') {
                    isPasswordFocusedRef.current = false;
                    hasPasswordBlurredRef.current = true;
                }
                setFocused((prev) => ({ ...prev, [field]: false }));

                if (input) {
                    const value = field === 'checkbox' ? input.checked : input.value;
                    const error = validateField(field, value);
                    if (field === 'password' && validatePasswordStrength && value !== '' && getLoadedPasswordScorer()) {
                        lastScoredPasswordRef.current = { value: value as string, error };
                    }

                    // Mark as touched and set error on blur
                    setTouched((prev) => ({ ...prev, [field]: true }));
                    setErrors((prev) => ({ ...prev, [field]: error }));
                }
                checkFormValidity();
            };

            if (isPointerDownRef.current) {
                pendingBlurRef.current = applyBlur;
                return;
            }
            applyBlur();
        },
        [validateField, validatePasswordStrength, checkFormValidity],
    );

    useEffect(() => {
        const handlePointerDown = (): void => {
            // Score now so a click on the submit button is not swallowed by a still-disabled button.
            flushPendingScore();
            isPointerDownRef.current = true;
        };
        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Enter' && event.target === passwordInputRef.current) {
                // Synchronous so the form can submit on this very keypress.
                flushSync(() => flushPendingScore());
            }
        };
        const handlePointerUp = (): void => {
            isPointerDownRef.current = false;
            const pendingBlur = pendingBlurRef.current;
            pendingBlurRef.current = null;
            if (pendingBlur) {
                // Next task: pointerup, mouseup and click are dispatched together, so this runs
                // after the click has reached its target.
                setTimeout(pendingBlur, 0);
            }
        };
        document.addEventListener('pointerdown', handlePointerDown, true);
        document.addEventListener('pointerup', handlePointerUp, true);
        document.addEventListener('pointercancel', handlePointerUp, true);
        document.addEventListener('keydown', handleKeyDown, true);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true);
            document.removeEventListener('pointerup', handlePointerUp, true);
            document.removeEventListener('pointercancel', handlePointerUp, true);
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [flushPendingScore]);

    const handleInputChange = useCallback(() => {
        // Covers autofill, which fires input events without a focus event.
        if (passwordInputRef.current?.value) {
            ensureScorerLoaded();
        }
        // Check form validity for submit button without showing validation errors
        evaluateForm('typing');
    }, [ensureScorerLoaded, evaluateForm]);

    // Check validity on component mount
    useEffect(() => {
        checkFormValidity();
    }, [checkFormValidity]);

    return {
        isFormValid,
        errors,
        touched,
        focused,
        refs: {
            emailInputRef,
            passwordInputRef,
            checkboxRef,
        },
        handleInputChange,
        handleBlur,
        handleFocus,
        validateField,
    };
}
